<template>
    <div class="container grid grid-cols-12">
        <div class="col-span-12 lg:col-span-8 lg:col-start-3 lg:my-auto">
            <form novalidate class="tile max-w-full bg-white" @submit.prevent="handleSubmit">
                <div class="grid gap-4 lg:gap-6">
                    <div>
                        <h1 class="text-xl">{{ data.flow.header }}</h1>
                        <div v-html="data.flow.explanation" />
                    </div>
                    <hr class="text-black/10" />
                    <InlineAlert v-if="success" type="success">
                        {{ $t('passwordReset.checkEmail') }}
                    </InlineAlert>
                    <InlineAlert v-if="generalErrorMessage">
                        {{ generalErrorMessage }}
                    </InlineAlert>
                    <Text
                        v-model="form.emailAddress"
                        type="email"
                        name="email"
                        :error-message="formErrors.emailAddress"
                        :validation-type="formErrors.emailAddress ? 'error' : 'none'"
                        :required="true"
                        :label="$t('form.emailAddressLabel')" />
                    <button :disabled="loading" class="btn-primary-a" type="submit">
                        {{ data.flow.buttonText }}
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script lang="ts" setup>
import type { ResetPasswordContentItem } from '@su4r-frontend/api';
import { FetchError } from 'ofetch';

const { data } = defineProps<{
    data: ResetPasswordContentItem;
}>();

const { locale, t } = useI18n();
const loading = ref<boolean>(false);
const success = ref<boolean>(false);
const generalErrorMessage = ref<string>('');

const form = reactive({
    emailAddress: '',
});

const formErrors = reactive({
    emailAddress: '',
});

function clearErrorMessages(): void {
    formErrors.emailAddress = '';
}

function isFormValid(e: Event): boolean {
    if (!e.target) return false;
    const formElement = e.target as HTMLFormElement;

    Array.from(formElement).forEach((el) => {
        const element = el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

        if (element.name === 'email') {
            if (element.validity.valueMissing) {
                formErrors.emailAddress = t('form.formErrorRequired');
            }
            if (element.validity.typeMismatch) {
                formErrors.emailAddress = t('form.formErrorInvalidFormat');
            }
        }
    });

    return Object.values(formErrors).every((error) => error === '');
}

async function handleSubmit(e: Event): Promise<void> {
    e.preventDefault();
    success.value = false;
    clearErrorMessages();
    generalErrorMessage.value = '';
    if (!isFormValid(e)) return;

    loading.value = true;

    try {
        await $fetch('/api/account/password-reset/request', {
            method: 'POST',
            body: { emailAddress: form.emailAddress, culture: locale.value },
        });
        success.value = true;
    } catch (error: unknown) {
        if (error instanceof FetchError && error.data.status === HTTP_STATUS.NOT_FOUND) {
            generalErrorMessage.value = t('passwordReset.errorEmailNotFound');
        } else {
            generalErrorMessage.value = t('passwordReset.errorGeneral');
        }
    }
    loading.value = false;
}
</script>
